import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Container,
  CircularProgress,
  Alert
} from '@mui/material';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import PaymentMethodSelector from '../components/PaymentMethodSelector';

interface PaymentState {
  formData: any;
  amount: number;
  formTitle: string;
  formId?: string;
}

const PaymentPage: React.FC = () => {
  const { customUrl } = useParams<{ customUrl: string }>();
  const location = useLocation();
  const state = location.state as PaymentState;

  const [error, setError] = useState<string | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentData, setPaymentData] = useState<PaymentState | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 2;

  useEffect(() => {
    // Check if we have the required data
    if (!state || !state.formData || !state.amount) {
      // Try to get from session storage
      const pendingSubmission = sessionStorage.getItem('pendingFormSubmission');
      if (!pendingSubmission) {
        setError('Invalid payment session. Please submit the form again.');
        return;
      }
      
      try {
        const submissionData = JSON.parse(pendingSubmission);
        if (submissionData.customUrl !== customUrl) {
          setError('Payment session mismatch. Please submit the form again.');
          return;
        }
        setPaymentData(submissionData);
      } catch (e) {
        setError('Invalid payment session. Please submit the form again.');
      }
    } else {
      setPaymentData(state);
    }
  }, [customUrl, state]);

  const handlePaymentMethodSelect = async (method: string, data?: any) => {
    if (!paymentData) {
      setError('Invalid payment session');
      return;
    }

    await attemptPayment(method, data);
  };

  const attemptPayment = async (method: string, data?: any, attempt: number = 0) => {
    setProcessingPayment(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('formId', paymentData!.formId || '');
      
      // Add each form field individually instead of as JSON string
      Object.keys(paymentData!.formData).forEach(fieldKey => {
        const value = paymentData!.formData[fieldKey];
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            // For array values (checkboxes, multi-select), send as JSON string
            formData.append(fieldKey, JSON.stringify(value));
          } else {
            formData.append(fieldKey, String(value));
          }
        }
      });
      
      formData.append('paymentMethod', method);

      if (method === 'bkash' && data?.paymentID) {
        formData.append('bkashPaymentId', data.paymentID);
      } else if (method === 'bank_transfer' && data?.receipt) {
        formData.append('payment_receipt', data.receipt);
        if (data.notes) {
          formData.append('bankTransferNotes', data.notes);
        }
      }

      // Submit form with payment method
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/submissions`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Clear session storage
      sessionStorage.removeItem('pendingFormSubmission');

      console.log('Payment successful:', response.data);

      // Navigate to success page with submission data
      const successUrl = `/success/${customUrl}?success=true&message=${encodeURIComponent('Form submitted and payment processed successfully!')}&submissionId=${response.data.submissionId}&uniqueId=${response.data.uniqueId}&editCode=${response.data.editCode}`;
      
      window.location.href = successUrl;

    } catch (error: any) {
      console.error('Payment processing error:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Failed to process payment. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 500 && attempt < MAX_RETRIES) {
        console.log(`Payment attempt ${attempt + 1} failed, retrying...`);
        setRetryCount(attempt + 1);
        setTimeout(() => {
          attemptPayment(method, data, attempt + 1);
        }, 2000); // Wait 2 seconds before retry
        return;
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please refresh the page and try again.';
      }
      
      setError(errorMessage);
      setRetryCount(0); // Reset retry count on final failure
    } finally {
      setProcessingPayment(false);
    }
  };

  if (error && !paymentData) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Alert severity="error">
            {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  if (!paymentData) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box py={4}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Payment Required
        </Typography>
        
        <Typography variant="h6" gutterBottom align="center" color="textSecondary">
          {paymentData.formTitle}
        </Typography>

        <Box mt={4}>

          <PaymentMethodSelector
            amount={paymentData.amount || 0}
            onPaymentMethodSelect={handlePaymentMethodSelect}
            loading={processingPayment}
            error={error || undefined}
            formData={paymentData.formData}
            formId={paymentData.formId}
            customUrl={customUrl}
          />
          
          {retryCount > 0 && processingPayment && (
            <Box mt={2} textAlign="center">
              <Typography variant="body2" color="info.main">
                Retry attempt {retryCount} of {MAX_RETRIES}...
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default PaymentPage;
